import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonDatetime,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonRow,
  IonText,
  IonTitle,
  IonToolbar,
  IonLabel,
  IonModal,
  IonDatetimeButton,
} from '@ionic/react';
import { add, chevronForwardOutline, globeOutline } from 'ionicons/icons';
import { useMemo, useRef, useState } from 'react';
import { parseISO, format } from 'date-fns';
import RecurrenceSelect, { getRecurrenceRuleByOption } from './RecurrenceSelect';
import { RRule } from 'rrule';
import { _task } from '../../../utils/state/model/implementations/ImplementationFactory';
import { TaskDO } from '../../../utils/state/model/interfaces/displayObjects';
import DeleteConfirmation from '../../general/DeleteConfirmation';
import {
  CALENDAR_WINDOW_IN_YEARS,
  isCalendarInteractive,
  recurrenceHighlight,
  setDefaultTime,
} from '../utils/calendarUtils';
import { createRecurrenceRule } from '../utils/recurrenceUtils';
import { useCalendarView } from '../hooks/useCalendarView';
import { useRecoilValue } from 'recoil';
import { Language, settingsState } from '../../settings/settingsState';
import { recurrenceRuleToNaturalLanguage } from '../utils/naturalLanguage';
import { t } from 'i18next';
import TimezoneSelect from './TimezoneSelect';
import { DateTime } from 'luxon';
import { Transition } from 'react-transition-group';
import {
  timePickerEnterAnimation,
  timePickerLeaveAnimation,
  timezonePickerDivStyle,
  timezonePickerDivTransition,
  timezonePickerRowStyle,
  timezonePickerRowTransition,
  timezoneToggleButtonStyle,
  timezoneToggleButtonTransition,
} from '../utils/calendarAnimation';
import { writeAccessRestrictedState } from '../../../App';

const CALENDAR_MAX_YEAR = String(new Date().getFullYear() + CALENDAR_WINDOW_IN_YEARS);

interface CalendarViewProps {
  onCancel: () => void;
  tasks: TaskDO[];
}

type CalendarISODate = string | undefined | null | string[];

const CalendarView: React.FC<CalendarViewProps> = ({ onCancel, tasks }) => {
  const { isRestricted: isWriteAccessRestricted } = useRecoilValue(writeAccessRestrictedState);

  const [
    {
      calendarValue: calendarDate,
      highlightedDates: recurrenceDates,
      isTimePickerOn,
      recurrence,
      recurrenceOption,
      timezone,
      isTimezonePickerOn: isTimezoneEnable,
    },
    dispatch,
  ] = useCalendarView(tasks);
  const [isTimezoneMenuOpen, setIsTimezoneMenuOpen] = useState(false);
  const [isRepeatMenuOpen, setIsRepeatMenuOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isClearConfirmationOpen, setIsClearConfirmationOpen] = useState(false);

  const { language } = useRecoilValue(settingsState);

  const timePickerRef = useRef<HTMLDivElement>(null);
  const timezonePickerRef = useRef(null);
  const timezoneToggleRef = useRef(null);

  const highlightRecurrenceDates = useMemo(() => recurrenceHighlight(recurrenceDates), [recurrenceDates]);

  const calendarChangeHandler = (value: CalendarISODate) => {
    if (!value || Array.isArray(value)) return;
    dispatch({ type: 'SET_CALENDAR', payload: { calendarValue: value } });
  };

  const doneClickHandler = () => {
    if (isWriteAccessRestricted || !calendarDate) return onCancel();
    if (tasks.length > 1 && isConfirmationOpen === false) return setIsConfirmationOpen(true);

    // const dueDate = calendarDate as string; // we're not allowing multiple dates here, so no string[]
    const dueDate = DateTime.fromISO(calendarDate as string)
      .setZone(timezone, { keepLocalTime: true })
      .toISO();

    _task.bulkUpdateTasksRecurrence(
      tasks.map((task) => task.taskId),
      parseISO(dueDate as string),
      recurrence ?? null,
      timezone
    );

    onCancel();
  };

  const clearClickHandler = () => {
    if (tasks.length > 1 && isClearConfirmationOpen === false) return setIsClearConfirmationOpen(true);

    _task.bulkUpdateTasksRecurrence(
      tasks.map((task) => task.taskId),
      null,
      null,
      undefined
    );

    onCancel();
  };

  const cancelClickHandler = () => {
    onCancel();
  };

  const selectRecurrenceHandler = (value: string | RRule) => {
    if (!calendarDate) {
      dispatch({ type: 'SET_CALENDAR', payload: { calendarValue: setDefaultTime(new Date()) } });
    }

    if (typeof value !== 'string') {
      const rule = createRecurrenceRule(
        value,
        'custom',
        parseISO((calendarDate as string) ?? setDefaultTime(new Date())),
        timezone
      );
      dispatch({ type: 'SET_RRULE', payload: { recurrence: rule, recurrenceOption: 'custom' } });
      return;
    }
    const option = getRecurrenceRuleByOption(value);
    if (!option) {
      dispatch({ type: 'SET_RRULE', payload: { recurrence: null, recurrenceOption: 'none' } });
      return;
    }

    const rule = createRecurrenceRule(
      option,
      value,
      parseISO((calendarDate as string) ?? setDefaultTime(new Date())),
      timezone
    );
    dispatch({ type: 'SET_RRULE', payload: { recurrence: rule, recurrenceOption: value } });
  };

  const timePickerToggleHandler = () => {
    dispatch({ type: 'TOGGLE_TIMEPICKER', payload: {} });
    if (isTimePickerOn) timePickerLeaveAnimation(timePickerRef);
    if (!isTimePickerOn) timePickerEnterAnimation(timePickerRef);
  };

  return (
    <>
      {/* Modal Header */}
      <IonHeader className="ion-padding-top ion-no-border" style={{ background: 'var(--toolbar-background)' }}>
        <IonToolbar style={{ '--background': 'var(--toolbar-background)', '--ion-safe-area-top': '0px' }}>
          <IonGrid>
            <IonRow className="ion-justify-content-around">
              <IonCol>
                <IonButtons>
                  <IonButton
                    onClick={clearClickHandler}
                    fill="clear"
                    disabled={isWriteAccessRestricted || !calendarDate}
                    color="error"
                  >
                    <IonText className="font-20">{t('calendarView.clear')}</IonText>
                  </IonButton>
                </IonButtons>
              </IonCol>
              <IonCol>
                <IonTitle style={{ textAlign: 'center' }}>{t('dueDate')}</IonTitle>
              </IonCol>
              <IonCol>
                <IonTitle class="ion-text-end" style={{ width: 'content' }}>
                  {calendarDate && format(parseISO(calendarDate as string), 'M/d/yy')}
                </IonTitle>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonToolbar>
      </IonHeader>

      {/* Modal Content */}
      <IonContent className="ion-padding-horizontal" style={{ '--background': 'var(--toolbar-background)' }}>
        {/* Content Grid Wrapper */}
        <IonGrid>
          {/* Calendar */}
          <IonDatetime
            mode="ios"
            locale={language === undefined || language === 'en' ? 'en-US' : 'es-ES'}
            value={calendarDate}
            min="2022"
            max={CALENDAR_MAX_YEAR}
            readonly={recurrenceDates ? true : false}
            isDateEnabled={isCalendarInteractive}
            highlightedDates={highlightRecurrenceDates}
            style={{ maxWidth: 'none' }}
            presentation={'date'}
            onIonChange={(e) => calendarChangeHandler(e.detail.value)}
            disabled={isWriteAccessRestricted}
          />

          {/* Time Picker */}
          <IonRow class="ion-margin-top ion-align-items-center">
            <IonTitle color={!isWriteAccessRestricted && calendarDate ? 'white' : 'light'}>
              {t('calendarView.time')}
            </IonTitle>
            <div
              ref={timePickerRef}
              className={`d-flex ion-align-items-center ${isTimePickerOn ? 'visible' : 'hidden'}`}
            >
              {/* TimezoneButton */}
              <IonIcon
                style={{
                  fontSize: '1.5rem',
                  background: isTimezoneEnable ? 'var(--ion-color-overlay)' : '',
                  color: isTimezoneEnable ? 'var(--ion-color-dark)' : 'var(--ion-color-medium)',
                  padding: '6px 12px',
                  borderRadius: '0.5rem',
                  transition: '500ms ease-in-out 500ms',
                  transform: isTimezoneEnable ? 'translateX(0)' : 'translateX(50%)',
                }}
                color="light"
                icon={globeOutline}
                onClick={() => dispatch({ type: 'TOGGLE_TIMEZONE_PICKER', payload: {} })}
              />
              <Transition nodeRef={timezoneToggleRef} in={isTimezoneEnable} timeout={500}>
                {(state) => (
                  <IonIcon
                    ref={timezoneToggleRef}
                    style={{ ...timezoneToggleButtonStyle, ...timezoneToggleButtonTransition[state] }}
                    icon={add}
                    className={`ion-no-padding font-32`}
                    onClick={() => dispatch({ type: 'TOGGLE_TIMEZONE_PICKER', payload: {} })}
                  />
                )}
              </Transition>

              {/* DatePickerButton */}
              {isTimePickerOn && (
                <>
                  <IonDatetimeButton
                    datetime="datetime"
                    disabled={!isWriteAccessRestricted && calendarDate ? false : true}
                  />
                  <IonModal keepContentsMounted={true} style={{ alignItems: 'center', justifyContent: 'center' }}>
                    <IonDatetime
                      minuteValues={'0, 5, 10, 15,20, 25, 30, 35, 40, 45, 50, 55'}
                      mode="ios"
                      value={calendarDate}
                      locale={'en-US'}
                      presentation="time"
                      id="datetime"
                      onIonChange={(event) => {
                        dispatch({ type: 'SET_TIMEPICKER', payload: { timePickerValue: event.target.value } });
                      }}
                    />
                  </IonModal>
                </>
              )}
            </div>
            <IonItem
              className="ion-no-padding"
              disabled={!isWriteAccessRestricted && calendarDate ? false : true}
              lines="none"
              color="background-color"
              onClick={() => timePickerToggleHandler()}
            >
              <IonIcon
                icon={add}
                className={`transform-100 font-32 ${isTimePickerOn ? 'rotate45' : ''}`}
                style={{ cursor: 'pointer' }}
              />
            </IonItem>
          </IonRow>

          {/* Timezone Picker */}
          <Transition nodeRef={timezonePickerRef} in={isTimezoneEnable} timeout={500}>
            {(state) => (
              <div ref={timezonePickerRef} style={{ ...timezonePickerDivStyle, ...timezonePickerDivTransition[state] }}>
                <IonRow
                  ref={timezonePickerRef}
                  style={{ ...timezonePickerRowStyle, ...timezonePickerRowTransition[state] }}
                  className="ion-align-items-center ion-justify-content-between ion-nowrap ion-padding-start"
                  onClick={() => (isTimezoneEnable ? setIsTimezoneMenuOpen(true) : null)}
                >
                  <IonLabel
                    style={{ fontWeight: '500' }}
                    class="font-22 ion-padding-start ion-text-nowrap"
                    color={!isWriteAccessRestricted && calendarDate ? 'white' : 'light'}
                  >
                    {t('timeZone')}
                  </IonLabel>
                  <IonCol class="ion-padding-start" size="6">
                    {/* TODO: Handle long natural language text */}
                    <IonLabel class="font-16">
                      {`${DateTime.local().setZone(timezone).offsetNameShort} - ${
                        DateTime.local().setZone(timezone).offsetNameLong
                      }`}
                    </IonLabel>
                  </IonCol>
                  <IonIcon className="font-28 ion-padding-end" icon={chevronForwardOutline} />
                </IonRow>
              </div>
            )}
          </Transition>

          {/* Repeat Selection Picker*/}
          <IonRow
            onClick={() => !isWriteAccessRestricted && setIsRepeatMenuOpen(true)}
            class="ion-align-items-center ion-justify-content-between ion-nowrap ion-padding-start"
          >
            <IonCol>
              <IonLabel style={{ fontWeight: '500' }} color={!isWriteAccessRestricted ? 'white' : 'light'}>
                {t('calendarView.repeat')}
              </IonLabel>
            </IonCol>
            {recurrence && (
              <IonCol class="ion-text-right" size="7">
                {/* TODO: Handle long natural language text */}
                <IonLabel class="ion-text-wrap">
                  {recurrenceRuleToNaturalLanguage(recurrence, language ?? Language.ENGLISH)}
                </IonLabel>
              </IonCol>
            )}
            <IonIcon
              color={!isWriteAccessRestricted ? 'white' : 'light'}
              className="font-28 ion-text-right ion-padding-end"
              icon={chevronForwardOutline}
            />
          </IonRow>
        </IonGrid>
      </IonContent>

      {/* Footer btns */}
      <IonFooter class="ion-no-border">
        <div
          className="d-flex ion-justify-content-between"
          style={{ background: 'var(--ion-color-custom-overlay)', minHeight: '56px' }}
        >
          <IonButton fill="clear" onClick={cancelClickHandler}>
            {t('cancel')}
          </IonButton>
          <IonButton fill="clear" onClick={doneClickHandler}>
            {t('done')}
          </IonButton>
        </div>
      </IonFooter>

      <DeleteConfirmation
        isOpen={isConfirmationOpen}
        setIsOpen={setIsConfirmationOpen}
        deleteMe={doneClickHandler}
        confirmButtonText="Yes, apply the due date"
        header="Replace due dates?"
        message="Any existing due date will be replaced."
      />

      <DeleteConfirmation
        isOpen={isClearConfirmationOpen}
        setIsOpen={setIsClearConfirmationOpen}
        deleteMe={clearClickHandler}
        confirmButtonText="Yes, apply the due date"
        header="Replace due dates?"
        message="Any existing due date will be replaced."
      />

      <TimezoneSelect
        isOpen={isTimezoneMenuOpen}
        setIsOpen={setIsTimezoneMenuOpen}
        timezone={timezone as string}
        setTimezone={dispatch}
      />

      <RecurrenceSelect
        isOpen={isRepeatMenuOpen}
        setIsOpen={setIsRepeatMenuOpen}
        onSelect={selectRecurrenceHandler}
        recurrenceOption={recurrenceOption as string}
        recurrenceValue={recurrence as RRule | null}
        calendarDate={calendarDate ? parseISO(calendarDate as string) : null}
      />
    </>
  );
};

export default CalendarView;
